/** Types a prepared image may have when the app sets no `accept`. */
export const defaultAttachmentAccept = ["image/jpeg", "image/png", "image/webp"];
/** Bytes of a picked file before preparation (a phone photo is far below). */
export const maxSourceBytes = 25_000_000;
/** HEIC/HEIF (iPhone photos): accepted for preparation only, sent as JPEG. */
const preparedOnly = ["image/heic", "image/heif"];
/** AttachmentOptions with every default filled in. */
export function attachmentOptions(opts) {
    return {
        accept: opts?.accept?.length ? opts.accept : defaultAttachmentAccept,
        maxCount: opts?.maxCount ?? 3,
        maxBytes: opts?.maxBytes ?? 3_500_000,
        maxEdge: opts?.maxEdge ?? 1568,
        quality: opts?.quality ?? 0.85,
    };
}
/** The `accept` attribute of the file input: the accepted types plus HEIC/HEIF. */
export function acceptAttribute(opts) {
    return [...attachmentOptions(opts).accept, ...preparedOnly].join(",");
}
/**
 * The size of an image scaled to at most maxEdge on its long side. Never
 * upscales; rounds to whole pixels (at least 1). Pure.
 */
export function fitWithin(width, height, maxEdge) {
    const w = Math.max(0, width);
    const h = Math.max(0, height);
    const long = Math.max(w, h);
    if (long === 0)
        return { width: 0, height: 0 };
    if (long <= maxEdge)
        return { width: Math.round(w), height: Math.round(h) };
    const s = maxEdge / long;
    return { width: Math.max(1, Math.round(w * s)), height: Math.max(1, Math.round(h * s)) };
}
/**
 * Whether a picked file may be added as the count-th image of a message
 * (count = images already added): null when it may, else the text key of
 * the reason. Pure.
 */
export function checkAttachment(file, opts, count) {
    const o = attachmentOptions(opts);
    if (count >= o.maxCount)
        return "assistantAttachmentCount";
    const type = (file.type || "").toLowerCase();
    if (!type.startsWith("image/") || !(o.accept.includes(type) || preparedOnly.includes(type)))
        return "assistantAttachmentType";
    if (file.size > maxSourceBytes)
        return "assistantAttachmentSize";
    return null;
}
/** A refused image; key names the text of the reason. */
export class AttachmentError extends Error {
    key;
    constructor(key) {
        super(key);
        this.name = "AttachmentError";
        this.key = key;
    }
}
async function encode(src, size, mime, quality) {
    const draw = (ctx) => {
        if (mime === "image/jpeg") {
            // JPEG has no transparency: a transparent area would turn black
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, size.width, size.height);
        }
        ctx.drawImage(src, 0, 0, size.width, size.height);
    };
    if (typeof OffscreenCanvas !== "undefined") {
        const canvas = new OffscreenCanvas(size.width, size.height);
        const ctx = canvas.getContext("2d");
        if (!ctx)
            throw new AttachmentError("assistantAttachmentType");
        draw(ctx);
        return canvas.convertToBlob({ type: mime, quality });
    }
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext("2d");
    if (!ctx)
        throw new AttachmentError("assistantAttachmentType");
    draw(ctx);
    return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new AttachmentError("assistantAttachmentType"))), mime, quality));
}
/** The long side of the second, smaller encoding of an image that is too large. */
const retryEdge = 1280;
/**
 * The type an image is sent as (pure): a PNG (screenshots, transparency)
 * becomes WebP when the browser can encode it, everything else JPEG. Both are
 * types every vision model takes (Bedrock: png, jpeg, gif, webp); HEIC never
 * leaves the browser.
 */
export function outputType(inputType, webpEncodes) {
    return inputType.toLowerCase() === "image/png" && webpEncodes ? "image/webp" : "image/jpeg";
}
/**
 * Prepares a picked image in the browser: decodes it (EXIF orientation
 * applied, HEIC where the browser decodes it), scales it to maxEdge (default
 * 1568 px, the size vision models read best), and re-encodes it as JPEG at
 * quality, or a PNG as WebP (JPEG when the browser cannot encode WebP), which
 * drops its metadata (EXIF, GPS). When the result is above maxBytes it is
 * encoded once more at long side 1280 and quality 0.7. Throws AttachmentError:
 * assistantAttachmentType when it cannot be decoded, assistantAttachmentSize
 * when it stays too large.
 */
export async function prepareImage(file, opts) {
    const o = attachmentOptions(opts);
    let bitmap;
    try {
        bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    }
    catch {
        throw new AttachmentError("assistantAttachmentType");
    }
    try {
        let size = fitWithin(bitmap.width, bitmap.height, o.maxEdge);
        if (size.width === 0 || size.height === 0)
            throw new AttachmentError("assistantAttachmentType");
        let mime = outputType(file.type, true);
        let blob = await encode(bitmap, size, mime, o.quality);
        if (mime === "image/webp" && blob.type !== "image/webp") {
            // Safari encodes no WebP and returns a PNG instead
            mime = outputType(file.type, false);
            blob = await encode(bitmap, size, mime, o.quality);
        }
        if (blob.size > o.maxBytes) {
            size = fitWithin(bitmap.width, bitmap.height, Math.min(o.maxEdge, retryEdge));
            blob = await encode(bitmap, size, mime, 0.7);
        }
        if (blob.size > o.maxBytes)
            throw new AttachmentError("assistantAttachmentSize");
        return { blob, width: size.width, height: size.height, mime };
    }
    finally {
        bitmap.close();
    }
}
